import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { html as htmlLang } from '@codemirror/lang-html'
import { oneDark } from '@codemirror/theme-one-dark'
import { EditorView } from '@codemirror/view'
import { Button, Space } from 'antd'
import { CodeOutlined, ColumnWidthOutlined, EyeOutlined } from '@ant-design/icons'

type ViewMode = 'code' | 'preview' | 'split'

interface EmailHtmlEditorProps {
  content: string
  onChangeContent: (content: string) => void
  draggedParam?: string | null
}

function formatHtml(raw: string): string {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(raw, 'text/html')
    const INDENT = '  '
    const VOID_TAGS = new Set([
      'br', 'hr', 'img', 'input', 'meta', 'link', 'area',
      'base', 'col', 'embed', 'param', 'source', 'track', 'wbr',
    ])

    function serialize(node: Node, depth: number): string {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim() ?? ''
        return text ? INDENT.repeat(depth) + text + '\n' : ''
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return ''

      const el = node as Element
      const tag = el.tagName.toLowerCase()
      const attrs = Array.from(el.attributes)
        .map((a) => `${a.name}="${a.value}"`)
        .join(' ')
      const openTag = attrs ? `<${tag} ${attrs}>` : `<${tag}>`

      if (VOID_TAGS.has(tag)) return INDENT.repeat(depth) + openTag + '\n'

      const children = Array.from(node.childNodes)
        .map((child) => serialize(child, depth + 1))
        .join('')

      if (!children.trim()) {
        return INDENT.repeat(depth) + (attrs ? `<${tag} ${attrs}></${tag}>` : `<${tag}></${tag}>`) + '\n'
      }
      return INDENT.repeat(depth) + openTag + '\n' + children + INDENT.repeat(depth) + `</${tag}>` + '\n'
    }

    return Array.from(doc.body.childNodes)
      .map((child) => serialize(child, 0))
      .join('')
      .trim()
  } catch {
    return raw
  }
}

function getParamFromDrop(e: DragEvent | React.DragEvent): string | null {
  const param = e.dataTransfer?.getData('text/plain') ?? ''
  return param && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(param.trim()) ? param.trim() : null
}

export function EmailHtmlEditor({ content, onChangeContent, draggedParam }: EmailHtmlEditorProps) {
  const isDark = useRef(window.matchMedia('(prefers-color-scheme: dark)').matches).current
  const [viewMode, setViewMode] = useState<ViewMode>('split')
  const [code, setCode] = useState(() => formatHtml(content))
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const iframeCleanupRef = useRef<(() => void) | null>(null)

  const isInitialized = useRef(false)
  useEffect(() => {
    if (!isInitialized.current && content) {
      setCode(formatHtml(content))
      isInitialized.current = true
    }
  }, [content])

  const notifyParent = useCallback(
    (value: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => onChangeContent(value), 400)
    },
    [onChangeContent]
  )

  const viewModeRef = useRef<ViewMode>('split')
  useEffect(() => { viewModeRef.current = viewMode }, [viewMode])

  const handleCodeChange = useCallback(
    (value: string) => {
      setCode(value)
      notifyParent(value)
      if (viewModeRef.current === 'split') {
        const doc = iframeRef.current?.contentDocument
        if (doc?.body) {
          doc.designMode = 'off'
          doc.body.innerHTML = value
          doc.designMode = 'on'
        }
      }
    },
    [notifyParent]
  )

  const setupIframe = useCallback(
    (iframe: HTMLIFrameElement, htmlContent: string, editable: boolean) => {
      iframeCleanupRef.current?.()
      iframeCleanupRef.current = null

      const handleLoad = () => {
        const doc = iframe.contentDocument
        if (!doc) return

        if (editable) {
          doc.designMode = 'on'

          const handleInput = () => {
            const newHtml = doc.body?.innerHTML ?? ''
            setCode(formatHtml(newHtml))
            notifyParent(newHtml)
          }

          const handleDrop = (e: DragEvent) => {
            const param = getParamFromDrop(e)
            if (!param) return
            e.preventDefault()
            e.stopPropagation()
            const placeholder = `{{${param}}}`
            const sel = doc.getSelection()
            if (sel && sel.rangeCount > 0) {
              const range = sel.getRangeAt(0)
              range.deleteContents()
              range.insertNode(doc.createTextNode(placeholder))
              range.collapse(false)
            }
            const newHtml = doc.body?.innerHTML ?? ''
            setCode(formatHtml(newHtml))
            notifyParent(newHtml)
          }

          const handleDragOver = (e: DragEvent) => {
            e.preventDefault()
            e.dataTransfer && (e.dataTransfer.dropEffect = 'copy')
          }

          doc.addEventListener('input', handleInput)
          doc.addEventListener('drop', handleDrop)
          doc.addEventListener('dragover', handleDragOver)
          iframeCleanupRef.current = () => {
            doc.removeEventListener('input', handleInput)
            doc.removeEventListener('drop', handleDrop)
            doc.removeEventListener('dragover', handleDragOver)
          }
        } else {
          doc.designMode = 'off'
        }
      }

      iframe.addEventListener('load', handleLoad, { once: true })
      iframe.srcdoc = htmlContent
    },
    [notifyParent]
  )

  const isIframeEditing = useRef(false)
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe || isIframeEditing.current) return
    const editable = viewMode === 'preview' || viewMode === 'split'
    setupIframe(iframe, code, editable)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // mount only

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [])

  const handleSetViewMode = useCallback((mode: ViewMode) => {
    const iframe = iframeRef.current

    if ((viewMode === 'preview' || viewMode === 'split') && iframe?.contentDocument) {
      const htmlContent = iframe.contentDocument.body?.innerHTML ?? ''
      const formatted = formatHtml(htmlContent)
      setCode(formatted)
      onChangeContent(htmlContent)
      isIframeEditing.current = false

      const editable = mode === 'preview' || mode === 'split'
      if (iframe) setupIframe(iframe, htmlContent, editable)
    } else if (mode === 'preview' || mode === 'split') {
      isIframeEditing.current = true
      if (iframe) setupIframe(iframe, code, true)
    }

    setViewMode(mode)
  }, [viewMode, code, onChangeContent, setupIframe])

  const cmDropExtension = useMemo(() => EditorView.domEventHandlers({
    drop(event) {
      const param = getParamFromDrop(event as DragEvent)
      if (!param) return false
      event.preventDefault()
      return false
    },
  }), [])

  const editorViewRef = useRef<EditorView | null>(null)

  const handleCodePaneDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const param = getParamFromDrop(e)
    if (!param) return
    const placeholder = `{{${param}}}`
    const cmView = editorViewRef.current
    if (cmView) {
      const pos = cmView.posAtCoords({ x: e.clientX, y: e.clientY }) ?? cmView.state.selection.main.from
      cmView.dispatch({
        changes: { from: pos, insert: placeholder },
        selection: { anchor: pos + placeholder.length },
      })
      const newCode = cmView.state.doc.toString()
      setCode(newCode)
      notifyParent(newCode)
    }
  }, [notifyParent])

  const codeMirrorExtensions = useMemo(() => [htmlLang(), cmDropExtension], [cmDropExtension])

  const handleCreateEditor = useCallback((view: EditorView) => {
    editorViewRef.current = view
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid #d9d9d9', padding: '4px 8px', background: '#fafafa' }}>
        <span style={{ fontSize: 12, color: '#888', marginRight: 4 }}>Chế độ xem:</span>
        <Space size={4}>
          <Button
            size="small"
            type={viewMode === 'code' ? 'primary' : 'default'}
            icon={<CodeOutlined />}
            onClick={() => handleSetViewMode('code')}
          >
            HTML
          </Button>
          <Button
            size="small"
            type={viewMode === 'split' ? 'primary' : 'default'}
            icon={<ColumnWidthOutlined />}
            onClick={() => handleSetViewMode('split')}
          >
            Split
          </Button>
          <Button
            size="small"
            type={viewMode === 'preview' ? 'primary' : 'default'}
            icon={<EyeOutlined />}
            onClick={() => handleSetViewMode('preview')}
          >
            Preview
          </Button>
        </Space>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 500 }}>
        <div
          style={{
            display: viewMode === 'preview' ? 'none' : 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            borderRight: viewMode === 'split' ? '1px solid #d9d9d9' : undefined,
            width: viewMode === 'split' ? '50%' : '100%',
          }}
          onDragOver={(e) => { if (draggedParam) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
          onDrop={handleCodePaneDrop}
        >
          <CodeMirror
            value={code}
            height="500px"
            extensions={codeMirrorExtensions}
            theme={isDark ? oneDark : undefined}
            onChange={handleCodeChange}
            onCreateEditor={handleCreateEditor}
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLine: true,
              autocompletion: true,
            }}
          />
        </div>

        <div
          style={{
            display: viewMode === 'code' ? 'none' : 'flex',
            flexDirection: 'column',
            background: '#fff',
            width: viewMode === 'split' ? '50%' : '100%',
          }}
        >
          <iframe
            ref={iframeRef}
            title="Email preview"
            style={{ width: '100%', height: '100%', minHeight: 500, border: 0 }}
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
      </div>
    </div>
  )
}
